import { lazy } from "react";
import { Navigate } from "react-router-dom";
import { ProtectedRoute } from "./lib/auth";
import Dashboard from "./component/Dashboard/Dashboard";

// Public marketing / legal pages (served at the root domain, same bundle as the admin)
const PrivacyPolicyNew = lazy(() => import("./component/Premika/PrivacyPolicyNew"));
const Home = lazy(() => import("./component/Premika/Home"));
const Premikaabout = lazy(() => import("./component/Premika/Premikaabout"));
const Premikatopbar = lazy(() => import("./component/Premika/Premikatopbar"));
const RefundPolicy = lazy(() => import("./component/Premika/RefundPolicy"));
const Term = lazy(() => import("./component/Premika/Term"));
const ChildSafety = lazy(() => import("./component/Premika/ChildSafety"));
const DeleteDetails = lazy(() => import("./component/Premika/DeleteDetails"));
const WalletBalance = lazy(
  () => import("./component/Premika/Transaction/WalletBalance")
);
const Payment = lazy(() => import("./component/Premika/Transaction/Payment"));
const Successfully = lazy(
  () => import("./component/Premika/Transaction/Successfully")
);
const PlanSection = lazy(
  () => import("./component/Premika/Price Plan/PlanSection")
);
const Contact = lazy(() => import("./component/Premika/Contact"));
const ShippingPolicy = lazy(() => import("./component/Premika/ShippingPolicy"));
const PlayStore = lazy(() => import("./component/PlayStore"));

// Admin pages (only the ones backed by real backend routes)
const Credentials = lazy(() => import("./component/Credentials/Credentials"));
const CredentialsForm = lazy(
  () => import("./component/Credentials/CredentialsForm")
);
const CredentialsShow = lazy(
  () => import("./component/Credentials/CredentialsShow")
);
const Recharge = lazy(() => import("./component/Recharge/recharge"));
const RechargeBody = lazy(() => import("./component/Recharge/rechargeBody"));
const RechargeEdit = lazy(
  () => import("./component/Recharge/Recharge Edit/rechargeedit")
);
const Login = lazy(() => import("./component/Auth/login"));
const Password = lazy(() => import("./component/Auth/Password"));
const UserTableBody = lazy(() => import("./component/UserData/UserTablebody"));
const UserShow = lazy(() => import("./component/User/UserShow"));
const Sidebar = lazy(() => import("./component/Global/sidebar"));
const TestAccounts = lazy(() => import("./component/TestAccounts/TestAccounts"));

// Analytics (backend/docs/analytics/ANALYTICS_CONTRACT.md §8)
const AnalyticsOverview = lazy(() => import("./component/Analytics/Overview"));
const AnalyticsScreens = lazy(() => import("./component/Analytics/Screens"));
const AnalyticsElements = lazy(() => import("./component/Analytics/Elements"));
const AnalyticsFlows = lazy(() => import("./component/Analytics/Flows"));
const AnalyticsPaywall = lazy(() => import("./component/Analytics/Paywall"));
const AnalyticsRetention = lazy(
  () => import("./component/Analytics/Retention")
);
const AnalyticsDataQuality = lazy(
  () => import("./component/Analytics/DataQuality")
);
const Customer360 = lazy(() => import("./component/Analytics/Customer360"));
const AnalyticsInsight = lazy(() => import("./component/Analytics/Insight"));
const AnalyticsAcquisition = lazy(
  () => import("./component/Analytics/Acquisition")
);
const AnalyticsCampaigns = lazy(
  () => import("./component/Analytics/Campaigns")
);
const AnalyticsEconomics = lazy(
  () => import("./component/Analytics/Economics")
);

// Push campaigns (backend docs/CAMPAIGNS.md) — admin route group: admin / super_admin.
const CampaignsList = lazy(() => import("./component/Campaigns/CampaignsList"));
const CampaignForm = lazy(() => import("./component/Campaigns/CampaignForm"));
const CampaignDetail = lazy(() => import("./component/Campaigns/CampaignDetail"));

const ADMIN_ROLES = ["admin", "super_admin", "sub_admin"];
// Customer 360 reads one person's event history — admin / super_admin only.
const CUSTOMER_360_ROLES = ["admin", "super_admin"];
// The backend's `admin` route group admits exactly these two roles.
const CAMPAIGN_ROLES = ["admin", "super_admin"];

const wrapWithProtection = (
  element,
  role = null,
  // `permission` arg kept for call-site compatibility but intentionally NOT
  // enforced: the astrology-era permission strings are unreliable and could
  // bounce a valid admin to /unauthorized. Access is role-gated, which is
  // sufficient for this single-admin tool.
  // eslint-disable-next-line no-unused-vars
  permission,
  redirectTo = "/"
) => {
  if (!role) return element;
  return (
    <ProtectedRoute role={role} redirectTo={redirectTo}>
      {element}
    </ProtectedRoute>
  );
};

export const RoutesArray = [
  // 🌐 Public Routes

  {
    path: "/credentials",
    element: <Navigate to="/credentials/login" />,
  },
  {
    path: "/",
    element: <Premikatopbar />,
    children: [
      {
        index: true,
        title: "Premika - Home",
        description: "Welcome to Premika",
        element: <Home />,
      },
      {
        path: "about",
        title: "Premika - About",
        description: "Learn more about Premika",
        element: <Premikaabout />,
      },
      {
        path: "refund_policy",
        title: "Premika - Refund Policy",
        description: "Understand our refund policy",
        element: <RefundPolicy />,
      },
      {
        path: "privacy_policy",
        title: "Premika - Privacy Policy",
        description: "Read our privacy policy",
        element: <PrivacyPolicyNew />,
      },
      {
        path: "terms_and_conditions",
        title: "Premika - Terms and Conditions",
        description: "Read our terms and conditions",
        element: <Term />,
      },
      {
        path: "child_safety",
        title: "Premika - Child Safety",
        description: "Learn about child safety",
        element: <ChildSafety />,
      },
      {
        path: "delete_account",
        title: "Premika - Delete Account",
        description: "Delete your account",
        element: <DeleteDetails />,
      },
      {
        path: "pricing_plan",
        title: "Premika - Pricing Plan",
        description: "View our pricing plans",
        element: <PlanSection />,
      },
      {
        path: "contact_us",
        title: "Premika - Contact Us",
        description: "Get in touch with us",
        element: <Contact />,
      },
      {
        path: "shipping_policy",
        title: "Premika - Shipping Policy",
        description: "Read our shipping policy",
        element: <ShippingPolicy />,
      },
      {
        path: "app/*",
        title: "Premika - Play Store",
        description: "Visit our Play Store page",
        element: <PlayStore />,
      },
      {
        path: "my_wallet",
        title: "Premika - My Wallet",
        description: "View your wallet balance",
        element: <WalletBalance />,
      },
      {
        path: "payment",
        title: "Premika - Payment",
        description: "Make a payment",
        element: <Payment />,
      },
      {
        path: "payment-success",
        title: "Premika - Payment Success",
        description: "Payment was successful",
        element: <Successfully />,
      },
    ],
  },
  {
    path: "credentials/login",
    title: "Premika - Login",
    description: "Login to your account",
    element: <Login />,
  },
  {
    path: "credentials/password",
    title: "Premika - Forgot Password",
    description: "Reset your password",
    element: <Password />,
  },

  {
    path: "/admin",
    element: <Navigate to="/admin/user" />,
  },
  // 🔐 Admin Protected Routes — only pages backed by real backend endpoints
  {
    path: "/admin",
    element: wrapWithProtection(<Sidebar />, [
      "admin",
      "super_admin",
      "sub_admin",
    ]),
    children: [
      {
        path: "dashboard",
        title: "Dashboard",
        description: "Welcome to Premika, Admin Panel",
        element: wrapWithProtection(
          <Dashboard />,
          ["admin", "super_admin", "sub_admin"],
          "DASHBOARD"
        ),
      },
      {
        path: "user",
        title: "Users",
        description: "Users Page",
        element: wrapWithProtection(
          <UserTableBody />,
          ["admin", "super_admin", "sub_admin"],
          "USER"
        ),
      },
      {
        path: "user/user_detail",
        title: "User Details",
        description: "User Details Page",
        element: wrapWithProtection(
          <UserShow />,
          ["admin", "super_admin", "sub_admin"],
          "USER"
        ),
      },
      {
        path: "plan",
        title: "Plan",
        description: "plan Page",
        element: wrapWithProtection(
          <Recharge />,
          ["admin", "super_admin", "sub_admin"],
          "PLANS"
        ),
      },
      {
        path: "plan/create_plan",
        title: "Creating plan",
        description: "plan Creation Page",
        element: wrapWithProtection(
          <RechargeBody />,
          ["admin", "super_admin", "sub_admin"],
          "PLANS"
        ),
      },
      {
        path: "plan/plan_detail",
        title: "plan Details",
        description: "plan Details Page",
        element: wrapWithProtection(
          <RechargeEdit />,
          ["admin", "super_admin", "sub_admin"],
          "PLANS"
        ),
      },
      {
        path: "credentials",
        title: "Credentials",
        description: "Credentials Page",
        element: wrapWithProtection(
          <Credentials />,
          ["admin", "super_admin", "sub_admin"],
          "CREDENTIALS"
        ),
      },
      {
        path: "credentials/create_credentials",
        title: "Creating Credentials",
        description: "Credentials Creation Page",
        element: wrapWithProtection(
          <CredentialsForm />,
          ["admin", "super_admin", "sub_admin"],
          "CREDENTIALS"
        ),
      },
      {
        path: "credentials/credentials_details",
        title: "Credentials Details",
        description: "Credentials Details Page",
        element: wrapWithProtection(
          <CredentialsShow />,
          ["admin", "super_admin", "sub_admin"],
          "CREDENTIALS"
        ),
      },
      {
        path: "test_accounts",
        title: "Test Accounts",
        description: "Internal QA account tooling",
        element: wrapWithProtection(
          <TestAccounts />,
          ["admin", "super_admin"],
          "TEST_ACCOUNTS"
        ),
      },
      {
        path: "analytics",
        element: <Navigate to="/admin/analytics/overview" />,
      },
      {
        path: "analytics/overview",
        title: "Analytics Overview",
        description: "Analytics Overview Page",
        element: wrapWithProtection(<AnalyticsOverview />, ADMIN_ROLES, "ANALYTICS"),
      },
      {
        path: "analytics/screens",
        title: "Analytics Screens",
        description: "Analytics Screens Page",
        element: wrapWithProtection(<AnalyticsScreens />, ADMIN_ROLES, "ANALYTICS"),
      },
      {
        path: "analytics/elements",
        title: "Analytics Elements",
        description: "Analytics Elements Page",
        element: wrapWithProtection(<AnalyticsElements />, ADMIN_ROLES, "ANALYTICS"),
      },
      {
        path: "analytics/flows",
        title: "Analytics Flows",
        description: "Analytics Flows Page",
        element: wrapWithProtection(<AnalyticsFlows />, ADMIN_ROLES, "ANALYTICS"),
      },
      {
        path: "analytics/paywall",
        title: "Analytics Paywall & Plans",
        description: "Analytics Paywall Page",
        element: wrapWithProtection(<AnalyticsPaywall />, ADMIN_ROLES, "ANALYTICS"),
      },
      {
        path: "analytics/retention",
        title: "Analytics Retention",
        description: "Analytics Retention Page",
        element: wrapWithProtection(<AnalyticsRetention />, ADMIN_ROLES, "ANALYTICS"),
      },
      {
        path: "analytics/insight",
        title: "Analytics Insight",
        description: "Analytics Insight Page",
        element: wrapWithProtection(<AnalyticsInsight />, CUSTOMER_360_ROLES, "ANALYTICS"),
      },
      {
        path: "analytics/acquisition",
        title: "Analytics Acquisition",
        description: "Analytics Acquisition Page",
        element: wrapWithProtection(<AnalyticsAcquisition />, CUSTOMER_360_ROLES, "ANALYTICS"),
      },
      {
        path: "analytics/campaigns",
        title: "Analytics Campaigns & Ads",
        description: "Analytics Campaigns & Ads Page",
        element: wrapWithProtection(<AnalyticsCampaigns />, CUSTOMER_360_ROLES, "ANALYTICS"),
      },
      {
        path: "analytics/economics",
        title: "Analytics Unit Economics",
        description: "Analytics Unit Economics Page",
        element: wrapWithProtection(<AnalyticsEconomics />, CUSTOMER_360_ROLES, "ANALYTICS"),
      },
      {
        path: "analytics/data_quality",
        title: "Analytics Data Quality",
        description: "Analytics Data Quality Page",
        element: wrapWithProtection(
          <AnalyticsDataQuality />,
          ADMIN_ROLES,
          "ANALYTICS"
        ),
      },
      {
        path: "analytics/customer_360",
        title: "Customer 360",
        description: "Customer 360 Page",
        element: wrapWithProtection(
          <Customer360 />,
          CUSTOMER_360_ROLES,
          "ANALYTICS"
        ),
      },
      {
        path: "campaigns",
        title: "Push Campaigns",
        description: "Push campaigns, engine health and emergency stop",
        element: wrapWithProtection(<CampaignsList />, CAMPAIGN_ROLES, "CAMPAIGNS"),
      },
      {
        path: "campaigns/new",
        title: "New Push Campaign",
        description: "Create a push campaign",
        element: wrapWithProtection(<CampaignForm />, CAMPAIGN_ROLES, "CAMPAIGNS"),
      },
      {
        path: "campaigns/:id",
        title: "Push Campaign",
        description: "Push campaign detail",
        element: wrapWithProtection(<CampaignDetail />, CAMPAIGN_ROLES, "CAMPAIGNS"),
      },
      {
        path: "campaigns/:id/edit",
        title: "Edit Push Campaign",
        description: "Edit a push campaign",
        element: wrapWithProtection(<CampaignForm />, CAMPAIGN_ROLES, "CAMPAIGNS"),
      },
      {
        path: "*",
        element: <Navigate to="/admin/user" />,
      },
    ],
  },

  // 🔁 Catch-all route
  {
    path: "*",
    element: <Navigate to="/" />,
  },
];
